import { useState, useCallback, useRef, useEffect } from 'react';
import { Task, TaskStatus, ChecklistItem } from '../types';
import { db, supabase } from '../services';
import { useProject } from '../context/ProjectContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from './useToast';
import { generateShortId } from '../utils/formatters';
import { sanitizeInput } from '../utils/validation';
import { Role } from '../types';

// Timeout for mutations to prevent indefinite "Saving..." state
const MUTATION_TIMEOUT_MS = 15000; // 15 seconds

// Helper to wrap a promise with a timeout
const withTimeout = <T>(promise: Promise<T>, ms: number, errorMessage: string): Promise<T> => {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(errorMessage));
    }, ms);

    promise
      .then((result) => {
        clearTimeout(timer);
        resolve(result);
      })
      .catch((err) => {
        clearTimeout(timer);
        reject(err);
      });
  });
};

// Helper to validate session before mutations
const validateSession = async (): Promise<boolean> => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error || !session) {
      console.error('[useTaskActions] Session validation failed:', error?.message || 'No session');
      return false;
    }

    // Check if token is about to expire (within 60 seconds)
    const expiresAt = session.expires_at || 0;
    const now = Math.floor(Date.now() / 1000);
    if (expiresAt - now < 60) {
      console.log('[useTaskActions] Token expiring soon, refreshing...');
      const { error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) {
        console.error('[useTaskActions] Token refresh failed:', refreshError.message);
        return false;
      }
    }

    return true;
  } catch (e) {
    console.error('[useTaskActions] Session validation error:', e);
    return false;
  }
};

// TaskPriority type
type TaskPriority = 'Low' | 'Medium' | 'High';

// ============================================
// Types
// ============================================

export interface TaskFormState {
  title: string;
  description: string;
  assigneeId: string;
  priority: TaskPriority;
  estimateHours: number;
  deadline: string;
  checklist: ChecklistItem[];
}

export interface UseTaskActionsReturn {
  // Form state
  formState: TaskFormState;
  setFormState: React.Dispatch<React.SetStateAction<TaskFormState>>;
  resetForm: () => void;
  
  // Checklist actions
  newItemText: string;
  setNewItemText: React.Dispatch<React.SetStateAction<string>>;
  toggleChecklistItem: (id: string) => void;
  removeChecklistItem: (id: string) => void;
  addChecklistItem: () => void;
  
  // Task actions
  handleSave: () => Promise<boolean>;
  handleDelete: () => Promise<boolean>;
  handleMoveStatus: (newStatus: TaskStatus) => Promise<void>;
  
  // UI state
  isSaving: boolean;
  isDeleting: boolean;
  
  // Permissions
  isAdminOrManager: boolean;
  canEdit: boolean;
  
  // Completion animation
  justCompleted: boolean;
}

// ============================================
// Hook
// ============================================

export const useTaskActions = (task: Task): UseTaskActionsReturn => {
  const { currentUser } = useAuth();
  const { setTasks, addTimelineEvent, addNotification, users } = useProject();
  const { showSuccess, showError } = useToast();
  
  // Form state
  const [formState, setFormState] = useState<TaskFormState>({
    title: task.title,
    description: task.description,
    assigneeId: task.assigneeId,
    priority: task.priority,
    estimateHours: task.estimateHours,
    deadline: task.deadline,
    checklist: task.checklist || [],
  });
  
  // New checklist item input
  const [newItemText, setNewItemText] = useState('');
  
  // Loading states
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isMovingRef = useRef(false); // Track if status change is in progress
  
  // Completion animation
  const [justCompleted, setJustCompleted] = useState(false);
  const prevStatusRef = useRef(task.status);
  
  // Permissions
  const isAdminOrManager = 
    currentUser?.role === Role.ADMIN || 
    currentUser?.role === Role.MANAGER;
  
  const canEdit = isAdminOrManager || task.assigneeId === currentUser?.id;

  // Track status changes for animation
  useEffect(() => {
    if (prevStatusRef.current !== TaskStatus.DONE && task.status === TaskStatus.DONE) {
      setJustCompleted(true);
      const timer = setTimeout(() => setJustCompleted(false), 1500);
      return () => clearTimeout(timer);
    }
    prevStatusRef.current = task.status;
  }, [task.status]);

  // Sync form state when task changes externally
  useEffect(() => {
    setFormState({
      title: task.title,
      description: task.description,
      assigneeId: task.assigneeId,
      priority: task.priority,
      estimateHours: task.estimateHours,
      deadline: task.deadline,
      checklist: task.checklist || [],
    });
  }, [task]);

  // Reset form to original task values
  const resetForm = useCallback(() => {
    setFormState({
      title: task.title,
      description: task.description,
      assigneeId: task.assigneeId,
      priority: task.priority,
      estimateHours: task.estimateHours,
      deadline: task.deadline,
      checklist: task.checklist || [],
    });
    setNewItemText('');
  }, [task]);

  // Checklist actions
  const toggleChecklistItem = useCallback((id: string) => {
    setFormState(prev => ({
      ...prev,
      checklist: prev.checklist.map(item =>
        item.id === id ? { ...item, completed: !item.completed } : item
      ),
    }));
  }, []);

  const removeChecklistItem = useCallback((id: string) => {
    setFormState(prev => ({
      ...prev,
      checklist: prev.checklist.filter(item => item.id !== id),
    }));
  }, []);

  const addChecklistItem = useCallback(() => {
    const text = sanitizeInput(newItemText);
    if (!text) return;
    
    const newItem: ChecklistItem = {
      id: generateShortId(),
      text,
      completed: false,
    };
    
    setFormState(prev => ({
      ...prev,
      checklist: [...prev.checklist, newItem],
    }));
    setNewItemText('');
  }, [newItemText]);

  // Save task changes
  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!canEdit) return false;

    setIsSaving(true);

    const updatedTask: Task = {
      ...task,
      title: sanitizeInput(formState.title),
      description: sanitizeInput(formState.description),
      assigneeId: formState.assigneeId,
      priority: formState.priority,
      estimateHours: formState.estimateHours,
      deadline: formState.deadline,
      checklist: formState.checklist,
    };

    try {
      // Validate session before making the mutation
      const isSessionValid = await validateSession();
      if (!isSessionValid) {
        showError('Session expired. Please refresh the page and try again.');
        return false;
      }

      // Perform the update with a timeout to prevent indefinite "Saving..." state
      await withTimeout(
        db.updateTask(task.id, updatedTask),
        MUTATION_TIMEOUT_MS,
        'Save operation timed out. Please check your connection and try again.'
      );

      // Update local state
      setTasks(prev => prev.map(t => t.id === task.id ? updatedTask : t));

      // Add timeline event (non-blocking to not affect save completion)
      addTimelineEvent(
        task.projectId,
        'task_updated',
        `Task "${updatedTask.title}" details were updated.`
      ).catch(e => console.warn('Timeline event failed:', e));

      // Notify new assignee if task was reassigned (with push notification)
      if (formState.assigneeId !== task.assigneeId) {
        const isAssignedToSelf = formState.assigneeId === currentUser?.id;
        addNotification(
          '📝 Task Assigned',
          `You've been assigned: "${updatedTask.title}"`,
          formState.assigneeId,
          `/projects/${task.projectId}`,
          !isAssignedToSelf // Send push only if assigned to someone else
        ).catch(e => console.warn('Notification failed:', e));

        // Notify previous assignee that task was reassigned (no push)
        if (task.assigneeId !== currentUser?.id) {
          const newAssigneeName = users.find(u => u.id === formState.assigneeId)?.name || 'Someone';
          addNotification(
            'Task Reassigned',
            `"${updatedTask.title}" has been reassigned to ${newAssigneeName}`,
            task.assigneeId,
            `/projects/${task.projectId}`,
            false // No push for removal notification
          ).catch(e => console.warn('Notification failed:', e));
        }
      }

      showSuccess('Task updated successfully');
      return true;
    } catch (err: any) {
      console.error('Task save error:', err);

      // Provide more specific error messages
      if (err.message?.includes('timed out')) {
        showError('Save timed out. Please check your connection and try again.');
      } else if (err.message?.includes('Session') || err.code === '401' || err.code === 'PGRST301') {
        showError('Session expired. Please refresh the page.');
      } else {
        showError(err.message || 'Failed to update task');
      }

      return false;
    } finally {
      setIsSaving(false);
    }
  }, [task, formState, canEdit, currentUser?.id, users, setTasks, addTimelineEvent, addNotification, showSuccess, showError]);

  // Delete task
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!isAdminOrManager) return false;

    setIsDeleting(true);

    try {
      // Validate session before making the mutation
      const isSessionValid = await validateSession();
      if (!isSessionValid) {
        showError('Session expired. Please refresh the page and try again.');
        return false;
      }

      await withTimeout(
        db.deleteTask(task.id),
        MUTATION_TIMEOUT_MS,
        'Delete operation timed out. Please check your connection and try again.'
      );

      // Update local state
      setTasks(prev => prev.filter(t => t.id !== task.id));

      // Add timeline event (non-blocking)
      addTimelineEvent(
        task.projectId,
        'task_deleted',
        `Task "${task.title}" was permanently removed.`
      ).catch(e => console.warn('Timeline event failed:', e));

      showSuccess('Task deleted');
      return true;
    } catch (err: any) {
      console.error('Task delete error:', err);

      if (err.message?.includes('timed out')) {
        showError('Delete timed out. Please check your connection and try again.');
      } else if (err.message?.includes('Session') || err.code === '401' || err.code === 'PGRST301') {
        showError('Session expired. Please refresh the page.');
      } else {
        showError(err.message || 'Failed to delete task');
      }

      return false;
    } finally {
      setIsDeleting(false);
    }
  }, [task, isAdminOrManager, setTasks, addTimelineEvent, showSuccess, showError]);

  // Move task to different status
  const handleMoveStatus = useCallback(async (newStatus: TaskStatus): Promise<void> => {
    if (task.status === newStatus) return;
    if (isMovingRef.current) return; // Prevent double-clicks

    isMovingRef.current = true;
    const previousStatus = task.status;

    // Optimistic update FIRST
    setTasks(prev => prev.map(t =>
      t.id === task.id ? { ...t, status: newStatus } : t
    ));

    try {
      // Validate session before making the mutation
      const isSessionValid = await validateSession();
      if (!isSessionValid) {
        // Rollback optimistic update
        setTasks(prev => prev.map(t =>
          t.id === task.id ? { ...t, status: previousStatus } : t
        ));
        showError('Session expired. Please refresh the page and try again.');
        return;
      }

      await withTimeout(
        db.updateTask(task.id, { status: newStatus }),
        MUTATION_TIMEOUT_MS,
        'Status update timed out. Please check your connection and try again.'
      );

      // Add timeline event (non-blocking)
      addTimelineEvent(
        task.projectId,
        'task_status_changed',
        `Task "${task.title}" moved to ${newStatus.replace('_', ' ')}.`
      ).catch(e => console.warn('Timeline event failed:', e));
    } catch (err: any) {
      console.error('Status change error:', err);
      // Rollback on error
      setTasks(prev => prev.map(t =>
        t.id === task.id ? { ...t, status: previousStatus } : t
      ));

      if (err.message?.includes('timed out')) {
        showError('Status update timed out. Please check your connection.');
      } else if (err.message?.includes('Session') || err.code === '401' || err.code === 'PGRST301') {
        showError('Session expired. Please refresh the page.');
      } else {
        showError('Failed to update task status');
      }
    } finally {
      isMovingRef.current = false;
    }
  }, [task, setTasks, addTimelineEvent, showError]);

  return {
    formState,
    setFormState,
    resetForm,
    newItemText,
    setNewItemText,
    toggleChecklistItem,
    removeChecklistItem,
    addChecklistItem,
    handleSave,
    handleDelete,
    handleMoveStatus,
    isSaving,
    isDeleting,
    isAdminOrManager,
    canEdit,
    justCompleted,
  };
};

export default useTaskActions;
